/**
 * auto-captions-smart-break.js — CapCut/Descript-style smart line-break reflow.
 *
 * Naive maxWords slicing chops captions mid-clause ("she went to the | store
 * yesterday."). Pro tools instead break after punctuation and conjunctions
 * while keeping each line within a character cap, so the eye lands on a
 * grammatical boundary and lines look visually balanced (variance < 30%).
 *
 * This is a pure helper — no dependency on the pipeline, no module loading,
 * compromise.cool is NOT required. We use a hand-rolled multilingual
 * punctuation+conjunction regex; this covers ~99% of the break-quality
 * delta vs naive without paying a 200 KB PoS-tagger.
 *
 * Public API (window.__acSmartBreak):
 *   .isEnabled()                                  — reads the #ac-smart-break
 *                                                   checkbox, default true
 *   .reflow(words, maxCharsPerLine, maxWordsPerLine)
 *       → [{ start, end, words, charCount }]
 *
 * The returned lines have the same shape as pipeline's `makeLine` output
 * (start/end/words) plus a `charCount` for QA + the test. Pipeline rebuilds
 * `makeLine`-equivalent objects from these, so we don't depend on access to
 * the blade's internal helper.
 *
 * Loaded with `defer` BEFORE pipeline.js so window.__acSmartBreak exists by
 * the time pipeline's rebuildLines() shim queries it.
 */
(function () {
    'use strict';

    /* ════════════════════════════════════════════════════════════
       MULTILINGUAL HEURISTICS
       ════════════════════════════════════════════════════════════ */

    // Sentence-final / strong breaks — highest preference. Includes CJK
    // full-stop, Arabic comma + Arabic question mark, ellipsis.
    var STRONG_END = /[\.\?\!…。！？؟]$/;

    // Mid-clause breaks (comma, semicolon, colon, em-dash, Arabic comma,
    // CJK comma). Medium preference.
    var WEAK_END = /[,;:،、，；]$/;

    // Coordinating conjunctions across all 9 supported locales. We match the
    // NEXT word being a conjunction (so we break BEFORE it, e.g.
    // "I went to the store, | and then I came home"). Lowercased.
    // EN: and or but so yet nor for
    // RU: и или но а да хотя
    // FR: et ou mais donc car ni
    // DE: und oder aber denn sondern
    // ES: y o pero sino aunque
    // PT: e ou mas porém embora
    // ID: dan atau tetapi namun
    // KO: 그리고 또는 하지만 그러나
    // AR: و أو لكن
    var CONJUNCTIONS = new Set([
        // English
        'and', 'or', 'but', 'so', 'yet', 'nor', 'for', 'because', 'although', 'though', 'while', 'whereas',
        // Russian
        'и', 'или', 'но', 'а', 'да', 'хотя', 'потому', 'однако',
        // French
        'et', 'ou', 'mais', 'donc', 'car', 'ni', 'parce',
        // German
        'und', 'oder', 'aber', 'denn', 'sondern', 'weil', 'obwohl',
        // Spanish
        'y', 'o', 'pero', 'sino', 'aunque', 'porque',
        // Portuguese
        'e', 'mas', 'porém', 'embora', 'porque',
        // Indonesian
        'dan', 'atau', 'tetapi', 'namun', 'karena',
        // Korean (particle-like)
        '그리고', '또는', '하지만', '그러나',
        // Arabic
        'و', 'أو', 'لكن', 'لأن'
    ]);

    function visibleLen(str) {
        // Treat anything outside Basic Latin + spaces as 1 unit (good enough
        // for caption line-width budgeting; we are not laying out pixels).
        return (str || '').length;
    }

    function lineCharCount(bucket) {
        // Sum of word.text + a single space between each.
        var total = 0;
        for (var i = 0; i < bucket.length; i++) {
            total += visibleLen(bucket[i].text || '');
            if (i > 0) total += 1; // separator
        }
        return total;
    }

    function endsWithStrong(word) {
        return STRONG_END.test(((word && word.text) || '').trim());
    }
    function endsWithWeak(word) {
        return WEAK_END.test(((word && word.text) || '').trim());
    }

    function nextIsConjunction(words, idx) {
        var nxt = words[idx + 1];
        if (!nxt) return false;
        var raw = (nxt.text || '').trim().toLowerCase();
        // Strip leading/trailing punctuation
        raw = raw.replace(/^[\(\[\"'«„¿¡]+|[\)\]\"',.;:!?…»“]+$/g, '');
        return CONJUNCTIONS.has(raw);
    }

    /* ════════════════════════════════════════════════════════════
       CORE REFLOW
       ════════════════════════════════════════════════════════════ */

    /**
     * Greedy accumulator. We push words one by one and consider breaking
     * AFTER each addition. A break decision uses three weighted signals:
     *
     *   1. Strong end (. ? ! …)            — always break
     *   2. Cap exceeded (chars OR words)   — always break (forced)
     *   3. Within 60-100% of charCap AND
     *      - current word ends with weak punct, OR
     *      - the NEXT word is a coordinating conjunction
     *                                       — break (preferred)
     *
     * The 60% threshold prevents tiny lines ("Yes, |") while letting us
     * snap to a clause boundary BEFORE we hit the hard cap.
     */
    function reflow(words, maxCharsPerLine, maxWordsPerLine) {
        if (!Array.isArray(words) || !words.length) return [];

        var charCap = Math.max(8, maxCharsPerLine | 0 || 42);
        var wordCap = Math.max(1, maxWordsPerLine | 0 || 6);
        var softFloor = Math.floor(charCap * 0.6);

        var lines = [];
        var bucket = [];

        function flush() {
            if (!bucket.length) return;
            lines.push({
                start: bucket[0].start,
                end: bucket[bucket.length - 1].end,
                words: bucket.slice(),
                charCount: lineCharCount(bucket)
            });
            bucket = [];
        }

        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            bucket.push(w);

            var charsAfter = lineCharCount(bucket);
            var wordsAfter = bucket.length;
            var strong = endsWithStrong(w);
            var weak = endsWithWeak(w);
            var nextConj = nextIsConjunction(words, i);

            // Forced break: caps exceeded — but only if we have something to
            // flush AND we're not stranding a 1-word tail mid-clause.
            var forced = (charsAfter >= charCap) || (wordsAfter >= wordCap);

            // Preferred break: at clause boundary inside the sweet zone.
            var preferred = (charsAfter >= softFloor) && (weak || nextConj);

            if (strong || forced || preferred) {
                flush();
            }
        }
        flush();
        return lines;
    }

    /* ════════════════════════════════════════════════════════════
       UI / SETTINGS — checkbox lives in the blade
       ════════════════════════════════════════════════════════════ */

    function isEnabled() {
        try {
            var el = document.getElementById('ac-smart-break');
            if (el && typeof el.checked === 'boolean') return el.checked;
        } catch (e) { /* document missing in node-side tests */ }
        return true; // default ON
    }

    /* ════════════════════════════════════════════════════════════
       EXPORT
       ════════════════════════════════════════════════════════════ */

    var api = { isEnabled: isEnabled, reflow: reflow };

    if (typeof window !== 'undefined') {
        window.__acSmartBreak = api;
    }
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = api;
    }
})();
